<script>
    // Licensed under Gnu Public Licence version 2
    // Copyright (c) 2021-2024 (original work) Open Assessment Technologies SA ;

    import { breakpoints } from '@oat-sa-private/ui-identity';
    import { IconBarButton } from '@oat-sa-private/ui-elements';
    import {
        generateElementId,
        getPointerEventCoords,
        remToPx,
        getRemSizePx,
        getDefaultRemSizePx,
        __,
        resizeObserver
    } from '@oat-sa-private/ui-core';
    import { createEventDispatcher, onMount } from 'svelte';
    import { resizeDirections, resizeVectors } from './util/directions.js';
    import { ensureResizeConstraints, ensureDragConstraints } from './util/constraints.js';

    /**
     * Action for header bar
     * @typedef {Object} Action
     * @property {String} key key used for button click action details
     * @property {String} label label used by IconBarButton
     * @property {String} ariaLabel ariaLabel used by IconBarButton
     * @property {String} icon icon used by IconBarButton
     * @property {Boolean} disabled determines if button is disabled
     */

    /**
     * Component is used to display draggable and resizeable modal panel
     * @property {String} id id of the modal
     * @property {String} title title of the modal
     * @property {Number} [left] default (starting) position by x-axis in pixels
     * @property {Number} [top] default (starting) position by y-axis in pixels
     * @property {Number} [width] width of modal in pixels
     * @property {Number} [height] height of modal in pixels
     * @property {Number} [minWidth] minimum width of modal in pixels
     * @property {Number} [minHeight] minimum height of modal in pixels
     * @property {String} [closeAriaLabel] aria label of close button
     * @property {Boolean} [resizable] allow to resize modal
     * @property {Action[]} [actions] optional array of header actions
     * @property {String} closeIcon optional name of the close button icon
     * @property {String} closeButtonPosition optional position of the close modal button
     * @fires close custom event fired when modal is closed
     * @fires action custom event fired when when component's action button is clicked.
     * @fires resize custom event fired when modal is resized
     * @fires move custom event fired when modal is moved
     */

    //actual starting size will be based on modal content size
    const defaultMinWidthRem = 40; //rem
    const defaultMinHeightRem = 13; //rem
    const defaultWidthRem = 62; //rem

    const actionButtonSizeRem = 5.5; //rem

    //fallback position, has to declare them separately because of the calculated style prop
    const defaultTop = 0; //px
    const defaultLeft = 0; //px

    export let id = generateElementId('draggable-modal');
    export let title;
    export let left; //px
    export let top; //px
    export let width; //px
    export let height; //px
    export let minWidth; //px
    export let minHeight; //px
    export let closeAriaLabel = __('Close Panel');
    export let resizable = true;
    export let actions = [];
    export let closeIcon = 'minus-16';
    export let closeButtonPosition = 'left';
    export let noRemScaling = false;

    //generated id for title to be used in aria-labelledby prop
    const titleId = generateElementId('draggable-modal-title');
    const closeButtonId = generateElementId('draggable-modal-close-button');

    let computedStyle = '';

    //window constraints
    let windowWidth;
    let windowHeight;

    //modal element binding
    let draggableModalElement;

    //modal's header element binding
    let headerElement;

    let headerHeight;

    //direction id of resize hitbox in action
    let direction;

    //box width and height
    let startWidth;
    let startHeight;

    //initial box position on resize/drag operation
    let startLeft;
    let startTop;

    //initial pointer position on resize/drag operation
    let startX;
    let startY;

    //flags the current operation
    let resizing = false;
    let dragging = false;

    const dispatch = createEventDispatcher();

    /**
     * If component should not scale when 1rem definition changes, use transform:scale to scale it backwards.
     * Here, the value of this backwards scale is defined.
     * @returns {Number}
     */
    function getTransformScale() {
        if (!noRemScaling) {
            return 1;
        }
        return getDefaultRemSizePx() / getRemSizePx();
    }

    //define initial sizing values
    let transformScale = getTransformScale();
    if (!left && left !== 0) {
        left = defaultLeft;
    }
    if (!top && top !== 0) {
        top = defaultTop;
    }
    if (!minWidth) {
        minWidth = remToPx(defaultMinWidthRem) * transformScale;
    }
    if (!minHeight) {
        minHeight = remToPx(defaultMinHeightRem) * transformScale;
    }
    if (typeof width === 'undefined') {
        width = remToPx(defaultWidthRem) * transformScale;
    }

    //computed prop calculations
    $: {
        if (!isMobile()) {
            //mention variables explicitly for reactive re-calculation
            width && height && top && left && minWidth && minHeight && windowWidth && windowHeight;

            computedStyle = `
            min-width: ${minWidth / transformScale}px;
            min-height: ${minHeight / transformScale}px;
            width: ${width ? `${width / transformScale}px` : 'unset'};
            height: ${height ? `${height / transformScale}px` : 'unset'};
            left: ${left}px;
            top: ${top}px;`;
        } else {
            computedStyle = `
            width: ${windowWidth / transformScale}px;
            height: ${windowHeight / transformScale}px;
            left: 0px;
            top: 0px;`;
        }
        //also add calculation of action containers - they need to be the same size
        computedStyle += ` --action-container-size: ${actionButtonSizeRem * Math.max(1, actions.length)}rem;`;

        if (transformScale !== 1) {
            computedStyle += `transform: scale(${transformScale});`;
            computedStyle += `transform-origin: top left;`;
        }
    }

    /**
     * Close modal button click handler
     * @param {UIEvent} e
     */
    function handleCloseClick(e) {
        dispatch('close');
        e.preventDefault();
    }

    /**
     * Returns appropriate handler for mousedown/touchstart
     * @param {String} [resizeDirection] direction of resize or none for drag move
     * @returns {Function} handler for mouse/touch event
     */
    function getPointerDownHandler(resizeDirection) {
        return handlePointerDown.bind(this, resizeDirection);
    }

    /**
     * Sets up inital vars for the means of resizing/dragging
     * @param {String} resizeDirection direction of resize or none for dragmove
     * @param {UIEvent} e
     */
    function handlePointerDown(resizeDirection, e) {
        //init width and height;
        const rect = draggableModalElement.getBoundingClientRect();
        if (!width) {
            width = rect.width;
        }
        if (!height) {
            height = rect.height;
        }

        const headerRect = headerElement.getBoundingClientRect();
        headerHeight = headerRect.height;

        //if left mouse button or touch
        if (e.buttons === 1 || (e.changedTouches && e.changedTouches.length === 1)) {
            startX = getPointerEventCoords(e).x;
            startY = getPointerEventCoords(e).y;
            startWidth = rect.width;
            startHeight = rect.height;
            startLeft = rect.x;
            startTop = rect.y;
            direction = resizeDirection;
            if (resizeDirection) {
                resizing = true;
            } else {
                dragging = true;
            }
        }

        //that's needed to prevent page scrolling on drag manipulations
        if (e.changedTouches && e.changedTouches.length === 1) {
            e.preventDefault();
        }
    }

    /**
     * Handles stop of resize/drag actions resetting related variables and firing appropriate events
     */
    function handlePointerUp() {
        if (resizing) {
            dispatch('resize', { width, height, top, left });
            resizing = false;
        }
        if (dragging) {
            dispatch('move', { top, left });
            dragging = false;
        }
        startX = void 0;
        startY = void 0;
        startLeft = void 0;
        startTop = void 0;
        startWidth = void 0;
        startHeight = void 0;
    }

    /**
     * Handler for pointer move which re-calculates size and position of modal
     * @param {UIEvent} e mouse/touch event
     */
    function handlePointerEvent(e) {
        //to disable background text selection in Safari
        e.preventDefault();

        if (!(e.buttons === 1 || (e.changedTouches && e.changedTouches.length === 1))) {
            handlePointerUp();
            return;
        }

        let { x, y } = getPointerEventCoords(e);
        let dx;
        let dy;
        if (resizing) {
            //ensure width/height constraints
            x = ensureResizeConstraints(
                x,
                startX,
                windowWidth,
                resizeVectors[direction][0],
                startLeft,
                startWidth,
                minWidth
            );
            y = ensureResizeConstraints(
                y,
                startY,
                windowHeight,
                resizeVectors[direction][1],
                startTop,
                startHeight,
                minHeight
            );

            dx = x - startX;
            dy = y - startY;

            width = startWidth + dx * resizeVectors[direction][0];
            height = startHeight + dy * resizeVectors[direction][1];
            left = startLeft - dx * (resizeVectors[direction][0] < 0 ? resizeVectors[direction][0] : 0);
            top = startTop - dy * (resizeVectors[direction][1] < 0 ? resizeVectors[direction][1] : 0);
        }

        if (dragging) {
            x = ensureDragConstraints(x, startX, startLeft, width, windowWidth);
            y = ensureDragConstraints(y, startY, startTop, headerHeight, windowHeight);
            left = startLeft + x - startX;
            top = startTop + y - startY;
        }
    }

    /**
     * Check if we are on mobile-sized screen
     * @returns {Boolean}
     */
    function isMobile() {
        return windowWidth <= breakpoints.width.small || windowHeight <= breakpoints.height.small;
    }

    /**
     * Emits action up to action button click
     * @param {String} key
     */
    function emitAction(key) {
        dispatch('action', { key });
    }

    onMount(() => {
        if (isMobile()) {
            document.getElementById(closeButtonId).focus();
        }
    });
</script>

<style>
    .draggable-modal {
        @define-mixin resize-hitbox-box $top: auto, $right: auto, $bottom: auto, $left: auto, $width: auto,
            $height: auto {
            top: $top;
            right: $right;
            bottom: $bottom;
            left: $left;
            width: $width;
            height: $height;
        }

        display: flex;
        position: fixed;
        user-select: none;
        box-shadow: 0 0 0.75rem 0.375rem rgba(0, 0, 0, 0.1);
        /*this z-index is to prevent component to overflow alerts*/
        z-index: calc(var(--layer-5) - 1);
        border-radius: var(--radius-medium);

        & .contents {
            position: relative;
            display: flex;
            flex-direction: column;
            width: 100%;
            flex: 1 1 0;
            border: var(--border-medium) solid var(--color-border-default);
            border-radius: var(--radius-medium);
            & .resize-hitbox {
                position: absolute;
                background-color: transparent;
                user-select: none;
            }
            & .hitbox-top-left {
                @add-mixin resize-hitbox-box -0.75rem, , , -0.75rem, 1rem, 1rem;
                cursor: nwse-resize;
            }
            & .hitbox-top {
                @add-mixin resize-hitbox-box -0.75rem, 0.25rem, , 0.25rem, , 1rem;
                cursor: ns-resize;
            }
            & .hitbox-top-right {
                @add-mixin resize-hitbox-box -0.75rem, -0.75rem, , , 1rem, 1rem;
                cursor: nesw-resize;
            }
            & .hitbox-right {
                @add-mixin resize-hitbox-box 0.25rem, -0.75rem, 0.25rem, , 1rem;
                cursor: ew-resize;
            }
            & .hitbox-bottom-right {
                @add-mixin resize-hitbox-box auto, -0.75rem, -0.75rem, , 1rem, 1rem;
                cursor: nwse-resize;
            }
            & .hitbox-bottom {
                @add-mixin resize-hitbox-box auto, 0.25rem, -0.75rem, 0.25rem, , 1rem;
                cursor: ns-resize;
            }
            & .hitbox-bottom-left {
                @add-mixin resize-hitbox-box auto, , -0.75rem, -0.75rem, 1rem, 1rem;
                cursor: nesw-resize;
            }
            & .hitbox-left {
                @add-mixin resize-hitbox-box 0.25rem, , 0.25rem, -0.75rem, 1rem;
                cursor: ew-resize;
            }

            & .header {
                cursor: grab;
                display: flex;
                flex: 0 0 6.5rem;
                &.dragging {
                    cursor: grabbing;
                }
                & .action-container-start {
                    margin-block: 0.25rem;
                    margin-inline: 0.25rem 1rem;
                    display: flex;
                    flex: 0 0 var(--action-container-size);
                    flex-direction: row;
                    justify-content: flex-start;
                    align-items: flex-start;
                }
                & .action-container-end {
                    margin-block: 0.25rem;
                    margin-inline: 1rem 0.25rem;
                    display: flex;
                    flex: 0 0 var(--action-container-size);
                    flex-direction: row;
                    justify-content: flex-end;
                    align-items: flex-start;
                }
                & .title-container {
                    line-height: 3rem;
                    font-size: var(--fontsize-heading);
                    font-weight: bold;
                    text-transform: uppercase;
                    text-align: center;
                    margin: 1.75rem 0 1.75rem 0;
                    flex: 1 1 0;
                    pointer-events: none;
                }
            }

            & .content {
                position: relative;
                overflow: hidden;
                background-color: var(--color-bg-default);
                flex: 1 1 100%;
                border-radius: 0 0 var(--radius-small) var(--radius-small);
            }
        }
    }

    .zoom-tracker {
        width: 1rem;
    }
</style>

<svelte:window
    bind:innerWidth={windowWidth}
    bind:innerHeight={windowHeight}
    on:mousemove={resizing || dragging ? handlePointerEvent : void 0}
    on:touchmove={resizing || dragging ? handlePointerEvent : void 0}
    on:mouseup={resizing || dragging ? handlePointerUp : void 0}
    on:touchend={resizing || dragging ? handlePointerUp : void 0} />

<div class="draggable-modal" {id} style={computedStyle} bind:this={draggableModalElement} aria-labelledby={titleId}>
    <div
        class="zoom-tracker visually-hidden"
        use:resizeObserver={{
            callback: () => {
                transformScale = getTransformScale();
            }
        }} />
    <div class="contents">
        <header
            class="header inverted"
            class:dragging
            bind:this={headerElement}
            on:mousedown|self={getPointerDownHandler()}
            on:touchstart|self={getPointerDownHandler()}>
            <div
                class="action-container-start"
                on:mousedown|self={getPointerDownHandler()}
                on:touchstart|self={getPointerDownHandler()}>
                {#if closeButtonPosition === 'left'}
                    <IconBarButton
                        id={closeButtonId}
                        size="base-16"
                        icon={closeIcon}
                        skin="draggable-modal-action"
                        label={closeAriaLabel}
                        ariaLabel={closeAriaLabel}
                        on:click={handleCloseClick} />
                {/if}
            </div>
            <div class="title-container" id={titleId}>{title}</div>
            <div
                class="action-container-end"
                on:mousedown|self={getPointerDownHandler()}
                on:touchstart|self={getPointerDownHandler()}>
                {#each actions as action}
                    <IconBarButton
                        size="base-16"
                        icon={action.icon}
                        skin="draggable-modal-action"
                        label={action.label}
                        ariaLabel={action.ariaLabel}
                        disabled={action.disabled}
                        on:click={() => {
                            emitAction(action.key);
                        }} />
                {/each}
                {#if closeButtonPosition === 'right'}
                    <IconBarButton
                        id={closeButtonId}
                        size="base-16"
                        icon={closeIcon}
                        skin="draggable-modal-action"
                        label={closeAriaLabel}
                        ariaLabel={closeAriaLabel}
                        on:click={handleCloseClick} />
                {/if}
            </div>
        </header>
        <div class="content">
            <slot {transformScale} />
        </div>
        {#if resizable}
            <div
                class="resize-hitbox hitbox-top-left"
                data-direction={resizeDirections.nw}
                on:mousedown={getPointerDownHandler(resizeDirections.nw)}
                on:touchstart={getPointerDownHandler(resizeDirections.nw)} />
            <div
                class="resize-hitbox hitbox-top"
                data-direction={resizeDirections.n}
                on:mousedown={getPointerDownHandler(resizeDirections.n)}
                on:touchstart={getPointerDownHandler(resizeDirections.n)} />
            <div
                class="resize-hitbox hitbox-top-right"
                data-direction={resizeDirections.ne}
                on:mousedown={getPointerDownHandler(resizeDirections.ne)}
                on:touchstart={getPointerDownHandler(resizeDirections.ne)} />
            <div
                class="resize-hitbox hitbox-right"
                data-direction={resizeDirections.e}
                on:mousedown={getPointerDownHandler(resizeDirections.e)}
                on:touchstart={getPointerDownHandler(resizeDirections.e)} />
            <div
                class="resize-hitbox hitbox-bottom-right"
                data-direction={resizeDirections.se}
                on:mousedown={getPointerDownHandler(resizeDirections.se)}
                on:touchstart={getPointerDownHandler(resizeDirections.se)} />
            <div
                class="resize-hitbox hitbox-bottom"
                data-direction={resizeDirections.s}
                on:mousedown={getPointerDownHandler(resizeDirections.s)}
                on:touchstart={getPointerDownHandler(resizeDirections.s)} />
            <div
                class="resize-hitbox hitbox-bottom-left"
                data-direction={resizeDirections.sw}
                on:mousedown={getPointerDownHandler(resizeDirections.sw)}
                on:touchstart={getPointerDownHandler(resizeDirections.sw)} />
            <div
                class="resize-hitbox hitbox-left"
                data-direction={resizeDirections.w}
                on:mousedown={getPointerDownHandler(resizeDirections.w)}
                on:touchstart={getPointerDownHandler(resizeDirections.w)} />
        {/if}
    </div>
</div>
